import { ArrowLeft, Check, Monitor, Moon, Sun } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  type ShellThemePreference,
  THEME_PREFERENCES,
} from "../../lib/theme-preferences";
import { useShellThemeStore } from "../../state/shell-theme-store";

type ThemeOption = {
  value: ShellThemePreference;
  icon: typeof Sun;
};

const THEME_OPTIONS: readonly ThemeOption[] = [
  {
    value: "light",
    icon: Sun,
  },
  {
    value: "dark",
    icon: Moon,
  },
  {
    value: "system",
    icon: Monitor,
  },
] as const;

interface ShellThemeSwitcherProps {
  onBack: () => void;
  onClose: () => void;
}

export const ShellThemeSwitcher = ({
  onBack,
  onClose,
}: ShellThemeSwitcherProps) => {
  const { t } = useTranslation("layout");
  const preference = useShellThemeStore((state) => state.preference);
  const resolvedTheme = useShellThemeStore((state) => state.resolvedTheme);
  const setPreference = useShellThemeStore((state) => state.setPreference);

  const handleThemeSelection = (nextPreference: ShellThemePreference) => {
    setPreference(nextPreference);
    onClose();
  };

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="flex w-full items-center gap-3 border-b border-border-subtle px-5 py-3 text-left text-sm font-medium text-content-primary transition-colors hover:bg-surface-secondary"
      >
        <ArrowLeft className="h-4 w-4 text-content-muted" />
        <span>{t("topbar.theme.selectTitle")}</span>
      </button>

      <div className="p-2">
        <div className="mb-1 px-2 pb-2 pt-1 text-xs font-medium uppercase tracking-[0.14em] text-content-muted">
          {t("topbar.theme.label")}
        </div>
        <div className="space-y-1">
          {THEME_PREFERENCES.map((themePreference) => {
            const option = THEME_OPTIONS.find(
              (themeOption) => themeOption.value === themePreference,
            );

            if (!option) {
              return null;
            }

            const OptionIcon = option.icon;
            const isActive = option.value === preference;
            const isSystem = option.value === "system";

            return (
              <button
                key={option.value}
                type="button"
                aria-pressed={isActive}
                onClick={() => {
                  handleThemeSelection(option.value);
                }}
                className="flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-left text-sm text-content-secondary transition-colors hover:bg-surface-secondary"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center text-content-muted">
                  <OptionIcon className="h-4 w-4" aria-hidden />
                </span>
                <div className="min-w-0 flex-1">
                  <p
                    className={
                      isActive
                        ? "font-semibold text-content-primary"
                        : "font-medium text-content-secondary"
                    }
                  >
                    {t(`topbar.theme.options.${option.value}.label`)}
                  </p>
                  <p className="text-xs text-content-muted">
                    {t(`topbar.theme.options.${option.value}.detail`)}
                  </p>
                </div>
                {isActive ? (
                  <Check className="h-4 w-4 text-state-success" aria-hidden />
                ) : null}
                {!isActive && isSystem ? (
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-content-muted">
                    {t(`topbar.theme.options.${resolvedTheme}.label`)}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
